import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import Collapsible from 'components/collapsible';
import Divider from 'components/divider';
import NumberField from 'components/number-field';
import Paper from 'components/paper';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { Copy, DollarSign, MinusCircle, PlusCircle, Trash } from 'lucide-react';
import { useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Prompt } from 'react-router-dom';
import { putStepTwo } from 'views/playground/features/bluviu-nutrition-r2/api/update-step-two';
import { useScenarioPlanState } from 'views/playground/features/bluviu-nutrition-r2/context/scenario-plan-context';
import { ScenarioPlan } from 'views/playground/features/bluviu-nutrition-r2/types/scenario-plan.type';
import { StepProps } from 'views/playground/features/bluviu-nutrition-r2/types/step-props.type';
import { UpdateStepTwoInput } from 'views/playground/features/bluviu-nutrition-r2/types/update-step-two-input.type';

const StepTwo: React.FC<StepProps> = ({ onClick }) => {
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const theme = useTheme();

  const {
    scenarioPlanQuery: { data, isLoading },
  } = useScenarioPlanState();

  //#region Form
  const defaultValues = useMemo(() => {
    if (!!data?.stepTwoFields) {
      return data.stepTwoFields;
    }
    return {
      feedProgram: [
        {
          feed: '',
          fishSize: 0,
          price: 0,
        },
      ],
    };
  }, [data, isLoading]);

  const { control, handleSubmit, formState } = useForm<UpdateStepTwoInput>({
    defaultValues: defaultValues, // step default form values
  });

  const { fields, remove, prepend } = useFieldArray({
    control,
    name: 'feedProgram',
  });

  //#endregion Form

  const updateStepTwo = usePost<ScenarioPlan, UpdateStepTwoInput>(
    (request) => putStepTwo(request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Success!' },
      });

      queryClient.invalidateQueries({
        queryKey: ['getPlaygroundScenarioPlan']
      });
    },
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Fail!' },
      });
    }
  );

  const handleUpdate = (data: UpdateStepTwoInput) => {
    updateStepTwo.mutate(data, {
      onSuccess: () => {
        onClick(2); // only pass if successful update
      },
    });
  };

  const isFormDirty = useMemo(() => formState.isDirty, [formState]);

  const rowActions = (idx: number) => {
    return (
      <Box display={'flex'} flexDirection={'row'} gap={0}>
        <IconButton
          sx={{
            color: theme.palette.darkBlue.main,
          }}
          title={'Copy'}
          onClick={() => console.log('copy')}>
          <Copy size={36} strokeWidth={1} />
        </IconButton>
        {idx === 0 ? (
          <IconButton
            sx={{
              color: theme.palette.darkBlue.main,
            }}
            title={'Add line'}
            onClick={() =>
              prepend({
                feed: '',
                fishSize: 0,
                price: 0,
              })
            }>
            <PlusCircle size={36} strokeWidth={1} />
          </IconButton>
        ) : (
          <IconButton
            sx={{
              color: theme.palette.darkBlue.main,
            }}
            title={'Add line'}
            onClick={() => remove(idx)}>
            <MinusCircle size={36} strokeWidth={1} />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <>
      <Prompt when={isFormDirty} message="You have unsaved changes. Are you sure you want to leave this page?" />
      <Paper
        sx={{
          height: '100%',
        }}>
        <form
          onSubmit={handleSubmit(handleUpdate)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 16,
          }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
              <Typography>Feed Programs</Typography>
              <Button size="small" disabled>
                New Feed Program
              </Button>
            </Box>
            <Collapsible
              label={
                <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography>Feed Program</Typography>
                  <Box>
                    <IconButton sx={{ color: theme.palette.darkBlue.main }}>
                      <Copy size={18} />
                    </IconButton>
                    <IconButton sx={{ color: theme.palette.darkBlue.main }}>
                      <Trash size={18} />
                    </IconButton>
                  </Box>
                </Box>
              }
              open>
              <Box display={'flex'} flexDirection={'column'} gap={4}>
                {fields.map((field, idx) => (
                  <Box display={'flex'} gap={2} key={field.id} width={'100%'}>
                    <TextField
                      dark
                      label={'Feed'}
                      fullWidth
                      controller={{
                        control,
                        name: `feedProgram.${idx}.feed`,
                        rules: {
                          required: true,
                        },
                      }}
                    />
                    <NumberField
                      fullWidth
                      label={'Fish size (g)'}
                      dark
                      controller={{ control, name: `feedProgram.${idx}.fishSize` }}
                    />
                    <NumberField
                      dark
                      fullWidth
                      label={'Price'}
                      controller={{
                        control,
                        name: `feedProgram.${idx}.price`,
                        rules: {
                          required: true,
                        },
                      }}
                      endAdornment={<DollarSign size={18} />}
                    />
                    {rowActions(idx)}
                  </Box>
                ))}
              </Box>
            </Collapsible>
          </Box>
          <Divider flexItem dark />
          <Box display={'flex'} flexDirection={'row'} gap={2} justifyContent={'flex-end'}>
            <Button onClick={() => onClick(0)}>Back</Button>
            <Button type="submit">Next</Button>
          </Box>
        </form>
      </Paper>
    </>
  );
};

export default StepTwo;
